import Joi from 'joi'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { format, parse } from 'date-fns'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../../../UI/Btn'
import Checkbox from '../../../../../../UI/Checkbox'
import DateTimeField from '../../../../../../UI/DateTimeField'
import TextField from '../../../../../../UI/TextField'

import SettingHint from '../SettingHint'
import TemplateLinks from '../TemplateLinks'

import styles from '../style.module.scss'

/*
 * Ankündigung des Jahresresets (Anforderung #49).
 *
 * Steht bewusst neben dem Reset selbst und nicht bei den übrigen Erinnerungen:
 * Angekündigt wird genau die Handlung, die eine Zeile tiefer ausgelöst wird.
 * Zwei getrennte Orte hätten ein Datum erlaubt, an dem niemand zurücksetzt —
 * und eine Ankündigung, die niemand eingehalten hätte.
 *
 * Die Werte müssen zu services/schoolYearReset/schedule.js im Backend passen:
 * Dort wird ein zweites Mal gelesen, welche Vorlaufstufe fällig ist.
 */

const DATE_FORMAT = 'yyyy-MM-dd'

const DEFAULTS = { enabled: false, date: null, daysBefore: [30, 7, 1] }

// Drei Stufen sind der brauchbare Kompromiss: eine Vorwarnung mit Zeit zum
// Handeln, eine Erinnerung in der letzten Woche, eine am Vortag. Mehr Felder
// hätten die Maske aufgebläht, weniger ließen die erste Warnung ungenutzt
// verstreichen.
const STAGE_LABELS = [
	'Erste Erinnerung [x] Tage vorher',
	'Zweite Erinnerung [x] Tage vorher',
	'Letzte Erinnerung [x] Tage vorher'
]

/*
 * Meldungen auf Deutsch und feldbezogen: Ohne eigene Texte zeigt Joi den
 * Rohtext samt Feldpfad an. Ein leeres Vorlauffeld ist ausdrücklich erlaubt —
 * dann entfällt diese Stufe. Es darf nur nicht als „0 Tage vorher"
 * durchgehen; das fängt das Backend ein zweites Mal ab.
 */
const stageRule = Joi.number().integer().min(1).max(365).allow('', null).messages({
	'number.base': 'Bitte eine Zahl von Tagen angeben oder das Feld leer lassen.',
	'number.integer': 'Bitte eine ganze Zahl von Tagen angeben.',
	'number.min': 'Mindestens 1 Tag Vorlauf — am Tag des Resets kommt die Erinnerung zu spät.',
	'number.max': 'Höchstens 365 Tage Vorlauf.'
})

const schema = Joi.object({
	schoolYearReset: Joi.object().keys({
		enabled: Joi.boolean(),
		/*
		 * Das Datum darf leer bleiben, solange nicht angekündigt wird — sonst
		 * ließe sich die Ankündigung nicht abschalten, ohne ein Datum zu
		 * erfinden. Die Bedingung steht deshalb um das ganze Feld herum: Ein
		 * `allow(null)` an der Wurzel bliebe auch im Pflichtfall gültig.
		 */
		date: Joi.when('enabled', {
			is: true,
			then: Joi.date().required().messages({
				'date.base': 'Bitte das geplante Datum des Jahresresets angeben.',
				'any.required': 'Bitte das geplante Datum des Jahresresets angeben.'
			}),
			otherwise: Joi.date().allow(null, '')
		}),
		stage1: stageRule,
		stage2: stageRule,
		stage3: stageRule
	})
})

/** Aus den drei Feldern wird die Liste, die das Backend liest. */
const daysBeforeOf = value =>
	[value.stage1, value.stage2, value.stage3]
		.map(entry => (entry === '' || entry === null || entry === undefined ? null : Number(entry)))
		.filter(entry => Number.isInteger(entry) && entry >= 1)
		.sort((a, b) => b - a)

export default function SchoolYearReset(props) {
	const { t } = useTranslation()
	const {
		register,
		reset,
		control,
		handleSubmit,
		formState: { errors }
	} = useForm({
		resolver: joiResolver(schema),
		/*
		 * Ohne das springt react-hook-form nach einer misslungenen Prüfung in
		 * das erste fehlerhafte Feld — und das ist hier der Datumswähler.
		 * Dessen Verweis ist die Komponente von react-datepicker, kein
		 * Eingabefeld: Der Sprung wirft "e.focus is not a function", und die
		 * Fehlermeldung erscheint gar nicht erst. Lieber keine Sprungmarke als
		 * keine Meldung.
		 */
		shouldFocusError: false
	})

	const onSubmit = form => {
		const value = form.schoolYearReset

		props
			.updateOptions({
				name: 'schoolYearReset',
				value: {
					enabled: Boolean(value.enabled),
					date: value.date ? format(value.date, DATE_FORMAT) : null,
					daysBefore: daysBeforeOf(value)
				}
			})
			.then(() => toast.success(t('Options was updated')))
	}

	useEffect(() => {
		const value = { ...DEFAULTS, ...(props.form?.schoolYearReset || {}) }
		const [stage1, stage2, stage3] = value.daysBefore || []

		reset({
			schoolYearReset: {
				enabled: Boolean(value.enabled),
				// Das Datum steht als reine Tagesangabe in der Datenbank. Über
				// `new Date("2027-06-30")` gelesen wäre es Mitternacht UTC und
				// damit je nach Zeitzone der Vortag.
				date: value.date ? parse(value.date, DATE_FORMAT, new Date()) : null,
				stage1: stage1 ?? '',
				stage2: stage2 ?? '',
				stage3: stage3 ?? ''
			}
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="schoolYearReset" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>Ankündigung des Jahresresets</h3>
				<SettingHint
					example="Beispiel: Reset am 30.06.2027, Erinnerungen 30, 7 und 1 Tag vorher — also am 31.05., am 23.06. und am 29.06."
					warning="Dieses Datum löst nichts aus. Es kündigt nur an; zurückgesetzt wird die Datenbank weiterhin von Hand im Abschnitt darunter. Wer das Datum verschiebt, beginnt die Zählung von vorn: Zu einem neuen Datum gehen alle Stufen erneut hinaus. Ist das Datum vorbei, endet die Erinnerung von selbst."
				>
					Kündigt den Jahresreset vorher an: Schüler:innen und Lehrkräfte bekommen eine Mitteilung in der
					Anwendung und eine E-Mail mit der Aufforderung, Urkunden, Berichte und Dokumente vorher
					herunterzuladen. Nach dem Reset sind diese Unterlagen weg — die Nutzungsbedingungen (Punkt 7) legen
					das Herunterladen ausdrücklich in ihre Verantwortung. Der nächtliche Lauf prüft um 03:00 Wiener
					Zeit, ob eine der Stufen unten erreicht ist, und verschickt sie einmalig. Werden mehrere Stufen auf
					einmal fällig — etwa weil das Datum erst kurz vorher eingetragen wurde — geht trotzdem nur eine
					Erinnerung hinaus. Unterstützer:innen bekommen keine: Ihre Profile überdauern den Reset.
				</SettingHint>
				<TemplateLinks
					items={[
						{ channel: 'internal', key: 'SCHOOL_YEAR_RESET_REMINDER' },
						{ channel: 'email', key: 'SCHOOL_YEAR_RESET_REMINDER' }
					]}
				/>
				<div className={styles.sectionContent}>
					<div className={styles.flexContainer}>
						<Checkbox label="Reset ankündigen" register={register('schoolYearReset.enabled')} />
					</div>

					<div className={styles.flexContainer}>
						<DateTimeField
							className={styles.field}
							control={control}
							label="Geplanter Jahresreset am"
							mb={20}
							name="schoolYearReset.date"
							error={errors.schoolYearReset?.date}
						/>
					</div>

					<div className={styles.flexContainer}>
						{/*
						 * Ein leeres Feld lässt die Stufe entfallen. Es darf nur
						 * nicht als 0 gelesen werden — dann käme die Erinnerung
						 * am Tag des Resets, wenn nichts mehr zu retten ist.
						 */}
						{STAGE_LABELS.map((label, index) => (
							<TextField
								key={label}
								className={styles.field}
								label={label}
								mb={20}
								register={register(`schoolYearReset.stage${index + 1}`)}
								error={errors.schoolYearReset?.[`stage${index + 1}`]}
							/>
						))}
					</div>

					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

SchoolYearReset.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
